import {Fragment, useEffect, useState} from "react";
import {clearTouchedFields, collectErrors, hasError, isDirty, useForm} from "@/components/form/Helpers.ts";
import Form from "@/components/form/Form.tsx";
import Field from "@/components/form/Field.tsx";
import SectionTitle from "@/components/editor/SectionTitle.tsx";
import Paginator from "@/components/editor/Paginator.tsx";
import Page from "@/components/editor/Page.tsx";
import {cn} from "@/lib/utils.ts";
import {Button} from "@/components/ui/button.tsx";
import FormActions from "@/components/form/FormActions.tsx";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group.tsx";
import {Label} from "@/components/ui/label.tsx";
import DatePicker from "@/components/ui/date-picker.tsx";
import TextField from "@/components/editor/fields/TextField.tsx";
import ThemeSelector from "@/components/editor/fields/ThemeSelector.tsx";
import SummaryEditor from "@/components/editor/fields/SummaryEditor.tsx";
import CompanySelectField from "@/components/editor/fields/CompanySelectField.tsx";
import FieldArray from "@/components/form/FieldArray.tsx";
import {Company} from "@/types/Company";
import {ArrowLeftIcon, CircleAlert, LoaderCircle, Printer, X} from "lucide-react";
import {toast} from "sonner";
import AttachmentEditor from "@/components/editor/AttachmentEditor.tsx";
import {Link, useBlocker, useParams} from "react-router";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import LojalitasTokekepzesEditor from "@/components/editor/fields/LojalitasTokekepzesEditor.tsx";
import TorzsgardaDialog from "@/components/editor/TorzsgardaDialog.tsx";
import MemberEditor from "@/components/editor/MemberEditor.tsx";
import NyugdijEditor from "@/components/editor/NyugdijEditor.tsx";
import Navigation from "@/components/editor/Navigation.tsx";
import PrintButton from "@/components/print-button.tsx";
import {HoverCard, HoverCardContent, HoverCardTrigger} from "@/components/ui/hover-card.tsx";
import FieldObject from "@/components/form/FieldObject.tsx";
import {useAuth} from "react-oidc-context";
import CurrencyField from "@/components/editor/fields/CurrencyField.tsx";
import Cegertekelo from "@/components/editor/Cegertekelo.tsx";

export default function Editor({initialValues, submit, attachments, setAttachments, reloadAttachments}: {
    initialValues: any,
    submit: (form: Dictionary<any>) => Promise<boolean>,
    attachments: Attachment[],
    setAttachments: (val: Attachment[]) => void,
    reloadAttachments: () => void
}) {
    const [formValue, setFormValue] = useForm();
    const [displayedPage, setDisplayedPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [company, setCompany] = useState<Company | undefined>(undefined);
    const params = useParams();
    const documentId = params.id;
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        setDisplayedPage(0)
        setCompany(undefined)
    }, [documentId])

    async function submitForm() {
        const success = await submit(formValue.fieldValues)
        if (success) {
            toast.success("Sikeres mentés")
            setFormValue('touchedFields', clearTouchedFields(formValue));
        } else {
            toast.error("Hiba történt a mentés során")
        }
    }

    const anyFieldSelected = () => {
        const themes = formValue.fieldValues["theme"]
        let cnt = 0;
        for (const [key] of Object.entries(themes ?? {})) {
            if (key !== "ai" && themes[key]) {
                cnt++;
            }
        }
        return cnt > 0;
    }

    const dirty = isDirty(formValue);
    const printable = !hasError(formValue.errors) && company && formValue.fieldValues["theme"] && anyFieldSelected()
    const saveable = dirty && printable

    const auth = useAuth();

    function print(press: boolean) {
        setPrinting(true);


        const str = JSON.stringify(formValue.fieldValues);
        const f = document.createElement('form');
        f.method = 'POST';
        f.target = '_blank';
        f.action = '/api/print/' + documentId + '?press=' + press + '&api_key=' + auth.user?.access_token;
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = 'form';
        hiddenField.value = str;
        f.appendChild(hiddenField);
        document.body.appendChild(f);
        f.submit();

        setPrinting(false);
    }

    const blocker = useBlocker(
        ({currentLocation, nextLocation}) =>
            dirty &&
            currentLocation.pathname !== nextLocation.pathname
    );

    return <>
        <Dialog open={blocker.state == "blocked"}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        Nem mentett változtatásai vannak. Szeretne menteni?
                    </DialogTitle>
                </DialogHeader>
                <DialogFooter>
                    <Button variant={"destructive"} onClick={blocker.proceed}>Kilépés mentés nélkül</Button>
                    <Button onClick={blocker.reset}>Mégse</Button>
                    <Button disabled={!saveable} variant={"constructive"} onClick={async () => {
                        await submitForm()
                        blocker.proceed?.()
                    }}>Mentés és kilépés</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Form formValue={formValue} onChange={setFormValue} initialValues={initialValues} onSubmit={submitForm}>
            <div className={"flex flex-col h-full"}>
                <div className={"w-full shrink-0 h-32 bg-card rounded-t"}>
                    <Navigation company={company} currentPage={displayedPage} setCurrentPage={setDisplayedPage}/>
                </div>
                <div
                    className={"grow overflow-y-auto px-24 py-4 flex flex-col gap-4 bg-card rounded-b border-t border-gray-300"}>
                    <div className={"h-full container mx-auto flex flex-col gap-4"}>
                        <Paginator page={displayedPage} setPageCount={setTotalPages}>
                            <Page>
                                <TextField name={"title"} label={"Tanulmány neve"}/>
                                <div className={'flex gap-4'}>
                                    <FieldObject name={"contact"}>
                                        <TextField name={"name"} label={"Kapcsolattartó neve"}/>
                                        <TextField name={"email"} label={"Kapcsolattartó email címe"}/>
                                    </FieldObject>
                                </div>

                                <div className={"flex gap-4"}>
                                    <CurrencyField name={"tanulmany_dij"} label={"Tanácsadási díj"}/>
                                    <div className={"w-full"}></div>
                                </div>

                                <CompanySelectField name={"company"} onCompanyLoad={setCompany}/>
                                <SectionTitle>Cég adózási formája</SectionTitle>
                                <Field name={"way_of_taxation"}>
                                    {(field) =>
                                        <RadioGroup value={field.value}>
                                            <div className={"flex w-full gap-8 justify-center"}>
                                                <div className={"flex gap-1 cursor-pointer"}
                                                     onClick={() => field.onChange("tao")}>
                                                    <RadioGroupItem id={"tao_radio"} value={"tao"}/>
                                                    <Label htmlFor={"tao_radio"}>Társasági adó</Label>
                                                </div>
                                                <div className={"flex gap-1 cursor-pointer"}
                                                     onClick={() => field.onChange("kiva")}>
                                                    <RadioGroupItem id={"kiva_radio"} value={"kiva"}/>
                                                    <Label htmlFor={"kiva_radio"}>Kisvállalati adó</Label>
                                                </div>
                                            </div>
                                        </RadioGroup>}
                                </Field>
                                <div className={"flex w-full gap-4 mt-4"}>
                                    <TextField name={"city"} label={"Hely"}/>
                                    <Field name={"kelt"}>
                                        {(field) => <DatePicker label={"Kelt"} {...field}/>}
                                    </Field>
                                </div>
                            </Page>
                            <Page>
                                <div className={"w-1/2"}>
                                    <Field name={"meeting_date"}>
                                        {(field) => <DatePicker label={"Találkozó időpontja"} {...field}/>}
                                    </Field>
                                </div>
                                <SummaryEditor company={company}/>
                            </Page>
                            <Page>
                                <ThemeSelector/>
                            </Page>
                            {
                                formValue.fieldValues["theme"]?.ai &&
                                <Page>
                                    <Cegertekelo attachments={attachments} reloadAttachments={reloadAttachments} documentId={documentId} company={company}/>
                                </Page>
                            }
                            {
                                formValue.fieldValues["theme"]?.lojalitas &&
                                <Page>
                                    <SectionTitle>Lojalitás program</SectionTitle>
                                    <Field name={"bizalmiIndex"}>
                                        {(field) => (
                                            <Label
                                                className={cn("flex gap-2 items-center")}>
                                                <Checkbox checked={field.value} onCheckedChange={field.onChange}/>
                                                <p className={"text-sm"}>Bizalmi index legyen megjelenítve</p>
                                            </Label>
                                        )}
                                    </Field>
                                    <LojalitasTokekepzesEditor name={"lojalitasArray"}/>
                                </Page>
                            }
                            {
                                formValue.fieldValues["theme"]?.torzsgarda &&
                                <Page>
                                    <SectionTitle>Törzsgárda program</SectionTitle>
                                    <TorzsgardaDialog/>
                                </Page>
                            }
                            {
                                formValue.fieldValues["theme"]?.tokekepzes &&
                                <Page>
                                    <div>
                                        <SectionTitle>Tulajdonosi tőkeképzés</SectionTitle>
                                        <div className={"mt-2"}>
                                            <Field name={"hozam"}>
                                                {(field) => (
                                                    <div className={"flex gap-1"}>
                                                        {
                                                            [
                                                                {
                                                                    value: "7.415",
                                                                    label: "7,4%"
                                                                },
                                                                {
                                                                    value: "10",
                                                                    label: "10%"
                                                                }
                                                            ].map((item) => <Button className={"min-w-20"}
                                                                                    key={item.value}
                                                                                    variant={field.value == item.value ? 'default' : 'outline'}
                                                                                    onClick={() => field.onChange(item.value)}>{item.label}</Button>)
                                                        }
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                    <LojalitasTokekepzesEditor name={"valutaArray"}/>
                                </Page>
                            }
                            <Page>
                                <SectionTitle>Cégképviseletben</SectionTitle>
                                <MemberEditor/>
                                <SectionTitle>További résztvevők</SectionTitle>
                                <FieldArray name={"tovabbiResztvevok"} end={(actions) => (
                                    <Button onClick={() => actions.add({})}>
                                        Résztvevő hozzáadása
                                    </Button>
                                )}>
                                    {(array) => (
                                        array.map((actions, index) => {
                                            return <>
                                                <div className={"flex gap-2 items-end"}>
                                                    <TextField name={"name"} label={"Név"}/>
                                                    <TextField name={"position"} label={"Titulus"}/>
                                                    <Button onClick={() => actions.remove(index)} variant={"outline"}
                                                            className={"mb-4"}><X/></Button>
                                                </div>
                                            </>
                                        })
                                    )}
                                </FieldArray>
                            </Page>
                            {
                                formValue.fieldValues["theme"]?.nyugdij &&
                                <Page>
                                    <SectionTitle>Nyugdíj program</SectionTitle>
                                    <NyugdijEditor/>
                                </Page>
                            }
                            <Page>
                                <SectionTitle>Csatolmányok</SectionTitle>
                                <AttachmentEditor attachments={attachments} setAttachments={setAttachments} reloadAttachments={reloadAttachments}/>
                            </Page>
                        </Paginator>
                    </div>
                </div>
                <div
                    className={"w-full py-4 grid grid-cols-[12rem_1fr_auto_auto_1fr_12rem] gap-4 px-8 container mx-auto"}>
                    <div>
                        {(printable) &&
                            <Button variant={'outline'} asChild><Link
                                to={`/documents/${documentId}`}><ArrowLeftIcon/> Áttekintés</Link></Button>}
                    </div>
                    <div></div>
                    <Button className={"w-32"} disabled={displayedPage == 0}
                            onClick={() => setDisplayedPage(p => p - 1)}>Előző</Button>
                    <Button className={"w-32"} disabled={displayedPage + 1 == totalPages}
                            onClick={() => setDisplayedPage(p => p + 1)}>Következő</Button>
                    <div></div>
                    <div className={"flex gap-4"}>
                        <PrintButton onPrint={() => {
                            print(true)
                        }}>
                            <Button disabled={!printable || printing} variant={"outline"}
                                    onClick={() => print(false)}>
                                {printing ? <LoaderCircle className={"animate-spin"}/> : <Printer/>}
                            </Button>
                        </PrintButton>

                        {
                            !printable ?
                                <HoverCard>
                                    <HoverCardTrigger asChild>
                                        <Button className={"w-32"} variant={"outline"}>
                                            <CircleAlert
                                                className={"text-destructive"}/>
                                        </Button>
                                    </HoverCardTrigger>
                                    <HoverCardContent asChild>
                                        <div className={"grid grid-cols-[auto_auto] w-fit gap-2"}>
                                            <div className={"col-span-2 font-bold"}>
                                                Kitöltési hibák:
                                            </div>
                                            {
                                                Object.entries(collectErrors(formValue.errors) ?? {}).map(([key, value]) => (
                                                    <Fragment key={key}>
                                                        <p>{key}</p>
                                                        <p>{value}</p>
                                                    </Fragment>
                                                ))
                                            }
                                            {
                                                !company && <><p>Cég nincs kiválasztva!</p><p></p></>
                                            }
                                            {
                                                !anyFieldSelected() && <><p>Nincs kiválasztva téma!</p><p></p> </>
                                            }
                                        </div>
                                    </HoverCardContent>
                                </HoverCard> : <FormActions>
                                    {(actions) => (
                                        <Button disabled={!saveable} className={"w-32"} variant={"constructive"}
                                                onClick={actions.submit}>Mentés</Button>
                                    )}
                                </FormActions>
                        }
                    </div>

                </div>
            </div>
        </Form>
    </>
}